import React, { useCallback, useRef } from "react";
import {
  Button,
  ButtonGroup,
  DataTable,
  DataTableHead,
  DataTableRow,
  DataTableCell as DataTableCellBase,
} from "@flixbus/honeycomb-react";
import { Icon, IconCopy, IconDelete } from "@flixbus/honeycomb-icons-react";
import { formatLastEditedAt } from "@/utils/formatDate";
import { deleteCalculationConfirmationPopupId } from "../DeleteCalculationConfirmationPopup/DeleteCalculationConfirmationPopup.component";
import CalculationInputListPaginationControls from "./CalculationInputListPaginationControls.component";
import styles from "./CalculationInputList.module.scss";
import type {
  CalculationEntry,
  CalculationInputListProps,
  DeleteCalculationCandidate,
} from "./CalculationInputList.types";

// Honeycomb exports the forwardRef render signature here, so narrow the local
// JSX surface.
type DataTableCellProps = {
  children?: React.ReactNode;
  scope?: "col" | "row";
};

const DataTableCell =
  DataTableCellBase as unknown as React.FC<DataTableCellProps>;

interface CalculationInputListRowProps {
  entry: CalculationEntry;
  deleteTargetRef?: React.RefObject<HTMLElement>;
  onOpen: (id: string) => void;
  onCopy: (id: string) => void;
  onDelete: (candidate: DeleteCalculationCandidate) => void;
}

const CalculationInputListRow: React.FC<CalculationInputListRowProps> = ({
  entry: {
    id,
    name,
    line,
    supplierName,
    planningYear,
    keyAccountManager,
    status,
    lastEditedAt,
    costCategories,
  },
  deleteTargetRef,
  onOpen,
  onCopy,
  onDelete,
}) => {
  const deleteButtonRef = useRef<HTMLButtonElement>(null!);

  const handleOpen = useCallback(() => {
    onOpen(id);
  }, [id, onOpen]);

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      // Only react to activations originating on the row itself, so keystrokes
      // inside the action buttons aren't hijacked into opening the calculation.
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen(id);
      }
    },
    [id, onOpen],
  );

  // The action buttons sit inside the clickable row; stop their clicks from
  // bubbling up and also triggering the row's open handler.
  const handleCopy = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onCopy(id);
    },
    [id, onCopy],
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (deleteTargetRef) {
        deleteTargetRef.current = deleteButtonRef.current;
      }

      onDelete({ id, name });
    },
    [deleteTargetRef, id, name, onDelete],
  );

  return (
    <DataTableRow
      role="button"
      tabIndex={0}
      aria-label={`Open ${name}`}
      extraClasses={styles.clickableRow}
      onClick={handleOpen}
      onKeyDown={handleRowKeyDown}
    >
      <DataTableCell scope="row">{name}</DataTableCell>
      <DataTableCell>{line}</DataTableCell>
      <DataTableCell>{supplierName}</DataTableCell>
      <DataTableCell>{planningYear}</DataTableCell>
      <DataTableCell>{keyAccountManager}</DataTableCell>
      <DataTableCell>{status}</DataTableCell>
      <DataTableCell>{formatLastEditedAt(lastEditedAt)}</DataTableCell>
      <DataTableCell>{costCategories.join(", ")}</DataTableCell>
      <DataTableCell>
        <ButtonGroup orientation="horizontal" gap="half">
          <Button
            display="square"
            appearance="link"
            aria-label={`Copy ${name}`}
            onClick={handleCopy}
          >
            <Icon InlineIcon={IconCopy} aria-hidden="true" />
          </Button>
          <Button
            display="square"
            appearance="link"
            aria-label={`Delete ${name}`}
            aria-haspopup="dialog"
            aria-controls={deleteCalculationConfirmationPopupId}
            innerRef={deleteButtonRef}
            onClick={handleDelete}
          >
            <Icon InlineIcon={IconDelete} aria-hidden="true" />
          </Button>
        </ButtonGroup>
      </DataTableCell>
    </DataTableRow>
  );
};

const CalculationInputList: React.FC<CalculationInputListProps> = ({
  entries,
  pagination,
  deleteTargetRef,
  onOpen,
  onCopy,
  onDelete,
}) => (
  <div className={styles.content}>
    <DataTable
      headers={
        <DataTableHead>
          <DataTableCell scope="col">Calculation name</DataTableCell>
          <DataTableCell scope="col">Line</DataTableCell>
          <DataTableCell scope="col">Supplier</DataTableCell>
          <DataTableCell scope="col">Planning year</DataTableCell>
          <DataTableCell scope="col">KAM</DataTableCell>
          <DataTableCell scope="col">Status</DataTableCell>
          <DataTableCell scope="col">Last edited</DataTableCell>
          <DataTableCell scope="col">Cost category</DataTableCell>
          <DataTableCell scope="col">Action</DataTableCell>
        </DataTableHead>
      }
      controls={
        pagination ? (
          <CalculationInputListPaginationControls pagination={pagination} />
        ) : undefined
      }
    >
      {entries.map((entry) => (
        <CalculationInputListRow
          key={entry.id}
          entry={entry}
          deleteTargetRef={deleteTargetRef}
          onOpen={onOpen}
          onCopy={onCopy}
          onDelete={onDelete}
        />
      ))}
    </DataTable>
  </div>
);

export default React.memo(CalculationInputList);
